import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Form, 
  Button, 
  Badge, 
  Nav,
  Alert, 
  ListGroup, 
  Table
} from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FaArrowLeft, 
  FaEdit, 
  FaSave, 
  FaVial, 
  FaHistory, 
  FaTimes, 
  FaServer,
  FaCheckCircle,
  FaTimesCircle, 
  FaPlus,
  FaCode,
  FaFileAlt,
  FaSync,
  FaList,
  FaCogs,
  FaProjectDiagram
} from 'react-icons/fa';
import * as api from '../services/api';
import Spinner from '../components/Spinner';
import './MicroAgentDevelopment.css';
import { Mermaid } from 'mdx-mermaid/Mermaid';
import { cacheService, CACHE_TTL } from '../utils/cacheService';

const MicroAgentDevelopment = () => {
  const { agentId, microAgentId } = useParams();
  const navigate = useNavigate();
  
  const [microAgent, setMicroAgent] = useState(null);
  const [parentAgent, setParentAgent] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [promptContent, setPromptContent] = useState('');
  const [environmentFunctions, setEnvironmentFunctions] = useState([]);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [environmentVariables, setEnvironmentVariables] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isDeploying, setIsDeploying] = useState({ dev: false, prod: false });
  const [activeTab, setActiveTab] = useState('prompts');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVarsEditor, setShowVarsEditor] = useState(false);
  const [showPromptsEditor, setShowPromptsEditor] = useState(false);
  const [showLambdaEditor, setShowLambdaEditor] = useState(false);
  const [lambdaFunctions, setLambdaFunctions] = useState({});
  const [selectedLambdaFunction, setSelectedLambdaFunction] = useState(null);
  const [lambdaFunctionCode, setLambdaFunctionCode] = useState('');
  const [metadataContent, setMetadataContent] = useState({});
  const [loadingLambda, setLoadingLambda] = useState(false);
  const [stateMachine, setStateMachine] = useState(null);
  const [loadingStateMachine, setLoadingStateMachine] = useState(false);
  const [mermaidDiagram, setMermaidDiagram] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState('DEV');
  const [configFiles, setConfigFiles] = useState([]);
  const [selectedConfigFile, setSelectedConfigFile] = useState(null);
  const [configContent, setConfigContent] = useState('');
  const [microConfigData, setMicroConfigData] = useState({});
  const [showConfigEditor, setShowConfigEditor] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Enhanced helper function to safely extract data from deeply nested response structures
  const extractFromResponse = (response, ...keys) => {
    if (!response) return null;
    
    console.log('Extracting from response structure:', Object.keys(response));
    
    const paths = [
      response,
      response.response,
      response.body,
      response.data,
      response.body?.data?.test_results?.endpoint_response,
      response.data?.test_results?.endpoint_response,
      (() => {
        try {
          if (response.body?.data?.test_results?.endpoint_response?.body && 
              typeof response.body.data.test_results.endpoint_response.body === 'string') {
            console.log('Found test_results with string body, parsing...');
            return JSON.parse(response.body.data.test_results.endpoint_response.body);
          }
          
          if (response.data?.test_results?.endpoint_response?.body && 
              typeof response.data.test_results.endpoint_response.body === 'string') {
            console.log('Found test_results with string body in data, parsing...');
            return JSON.parse(response.data.test_results.endpoint_response.body);
          }
          
          return null;
        } catch (e) {
          console.warn('Failed to parse nested JSON body:', e);
          return null;
        }
      })()
    ].filter(Boolean);
    
    for (const path of paths) {
      if (!path) continue;
      
      let currentPath = path;
      if (typeof currentPath === 'string') {
        try {
          currentPath = JSON.parse(currentPath);
        } catch (e) {
          continue;
        }
      }
      
      let value = currentPath;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
          value = value[key];
        } else {
          value = null;
          break;
        }
      }
      
      if (value !== null && value !== undefined) {
        console.log(`Successfully extracted data from path for keys: ${keys.join('.')}`);
        return value;
      }
    }
    
    console.warn(`Could not extract data for keys: ${keys.join('.')}`);
    return null;
  };

  const transformMicroAgentFromNew = (newMicroAgent, agentName = '') => {
    console.log('Transforming micro-agent from new API format:', newMicroAgent);
    
    return {
      id: newMicroAgent.micro_agent_id || newMicroAgent.uniqueIdentifier,
      uniqueIdentifier: String(newMicroAgent.micro_agent_id || newMicroAgent.uniqueIdentifier),
      agentNumber: newMicroAgent.micro_agent_number || newMicroAgent.agentNumber,
      name: newMicroAgent.name || newMicroAgent.microAgentName,
      microAgentName: newMicroAgent.name || newMicroAgent.microAgentName,
      description: newMicroAgent.description || newMicroAgent.microAgentDescription,
      microAgentDescription: newMicroAgent.description || newMicroAgent.microAgentDescription,
      parentAgentName: agentName || newMicroAgent.parentAgentName,
      commissioned: newMicroAgent.status === 'production' || newMicroAgent.commissioned,
      deployedInDev: newMicroAgent.deployed_development || newMicroAgent.deployedInDev,
      deployedInProd: newMicroAgent.deployed_production || newMicroAgent.deployedInProd,
      isMiniAgent: newMicroAgent.is_mini_agent || newMicroAgent.isMiniAgent,
      objective: newMicroAgent.objective,
      scope: newMicroAgent.scope,
      documentation: newMicroAgent.documentation,
      schema: newMicroAgent.schema,
      status: newMicroAgent.status,
      human_notes: newMicroAgent.human_notes,
      lambda_functions: newMicroAgent.lambda_functions || [],
      prompts: newMicroAgent.prompts || [],
      recent_executions: newMicroAgent.recent_executions || [],
      agent_id: newMicroAgent.agent_id
    };
  };

  // OPTIMIZATION 1: PARALLEL API CALLS ON PAGE LOAD
  useEffect(() => {
    const fetchMicroAgentDetails = async () => {
      if (!agentId || !microAgentId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('🚀 Starting parallel API calls...');
        const startTime = performance.now();
        
        // Check cache first for parent agent
        const parentAgentCacheKey = `parent_agent_${agentId}`;
        let cachedParentAgent = cacheService.get(parentAgentCacheKey);
        
        // Check cache for micro agent
        const microAgentCacheKey = `micro_agent_${agentId}_${microAgentId}`;
        let cachedMicroAgent = cacheService.get(microAgentCacheKey);

        // PARALLEL EXECUTION: Fetch all critical data simultaneously
        const [
          parentAgentResult,
          microAgentResult,
          promptsResult,
          environmentFunctionsResult
        ] = await Promise.allSettled([
          // 1. Parent agent fetch (with cache)
          cachedParentAgent 
            ? Promise.resolve({ status: 'fulfilled', value: cachedParentAgent, fromCache: true })
            : api.getAgents(),
          
          // 2. Micro agent fetch (with cache)
          cachedMicroAgent
            ? Promise.resolve({ status: 'fulfilled', value: cachedMicroAgent, fromCache: true })
            : (async () => {
                const resolvedAgentId = cachedParentAgent?.uniqueIdentifier || agentId;
                const microAgentPayload = {
                  micro_agent_id: microAgentId
                };
                if (resolvedAgentId) {
                  microAgentPayload.agent_id = resolvedAgentId;
                }
                return api.makeAuthenticatedRequest('POST', '/db/micro-agents', microAgentPayload);
              })(),
          
          // 3. Prompts fetch (with cache)
          (async () => {
            const promptsCacheKey = `prompts_${agentId}_${microAgentId}`;
            const cachedPrompts = cacheService.get(promptsCacheKey);
            if (cachedPrompts) {
              return { prompts: cachedPrompts, fromCache: true };
            }
            try {
              return await api.getPromptContent(agentId, 'micro-agent', microAgentId);
            } catch (err) {
              console.warn('Error fetching prompts (non-critical):', err);
              return { prompts: ['system-prompt', 'user-prompt', 'assistant-prompt'] };
            }
          })(),
          
          // 4. Environment functions fetch (with cache)
          (async () => {
            const envFuncCacheKey = `env_functions_${agentId}_${microAgentId}`;
            const cachedEnvFunc = cacheService.get(envFuncCacheKey);
            if (cachedEnvFunc) {
              return { environmentVariables: cachedEnvFunc, fromCache: true };
            }
            try {
              return await api.getEnvironmentVariables(agentId, 'micro-agent', microAgentId);
            } catch (err) {
              console.warn('Error fetching environment functions (non-critical):', err);
              return { environmentVariables: ['initialize', 'process', 'finalize', 'get-logs', 'web-interaction'] };
            }
          })()
        ]);

        const endTime = performance.now();
        console.log(`✅ All parallel API calls completed in ${(endTime - startTime).toFixed(2)}ms`);

        // Process parent agent result
        let parentAgentData = null;
        if (parentAgentResult.status === 'fulfilled') {
          const parentResponse = parentAgentResult.value;
          
          if (parentResponse.fromCache) {
            console.log('📦 Using cached parent agent');
            parentAgentData = parentResponse;
          } else if (parentResponse && parentResponse.success && parentResponse.agents) {
            parentAgentData = parentResponse.agents.find(a => 
              a.uniqueIdentifier === agentId || 
              a.agentName === agentId || 
              a.repoName === agentId
            );
            
            // Cache parent agent for 5 minutes
            if (parentAgentData) {
              cacheService.set(parentAgentCacheKey, parentAgentData, CACHE_TTL.MEDIUM);
            }
          }
          
          if (parentAgentData) {
            setParentAgent({
              id: parentAgentData.uniqueIdentifier,
              name: parentAgentData.agentName || parentAgentData.repoName,
              description: parentAgentData.agentDescription,
              repoName: parentAgentData.repoName || "AgentDeveloper",
              awsAccount: parentAgentData.awsAccountNumber || "851725399969",
              awsRegion: parentAgentData.awsRegion || "us-west-2",
              repoAccount: parentAgentData.repoAccount || "materassi",
              cdkQualifier: parentAgentData.cdkQualifier
            });
          }
        } else {
          console.warn('Parent agent fetch failed, using fallback');
          setParentAgent({
            id: agentId,
            name: "AgentDeveloper",
            repoName: "AgentDeveloper",
            awsAccount: "851725399969",
            awsRegion: "us-west-2",
            repoAccount: "materassi"
          });
        }

        // Process micro agent result
        if (microAgentResult.status === 'fulfilled') {
          const microResponse = microAgentResult.value;
          
          if (microResponse.fromCache) {
            console.log('📦 Using cached micro agent');
            const normalizedMicroAgent = microResponse;
            setMicroAgent(normalizedMicroAgent);
            
            // Fetch config files if we have agent number
            if (normalizedMicroAgent.agentNumber) {
              fetchMicroConfigFiles(normalizedMicroAgent.agentNumber);
            }
          } else {
            let apiResponse = microResponse;
            if (microResponse.body?.data?.test_results?.endpoint_response?.body) {
              try {
                apiResponse = typeof microResponse.body.data.test_results.endpoint_response.body === 'string'
                  ? JSON.parse(microResponse.body.data.test_results.endpoint_response.body)
                  : microResponse.body.data.test_results.endpoint_response.body;
              } catch (parseError) {
                console.warn('Failed to parse test_results for micro-agent:', parseError);
              }
            }
            
            let microAgentsArray = extractFromResponse(apiResponse, 'data', 'micro_agents');
            
            if (microAgentsArray && Array.isArray(microAgentsArray) && microAgentsArray.length > 0) {
              const microAgentData = microAgentsArray.find(ma => 
                String(ma.micro_agent_id) === String(microAgentId) ||
                String(ma.id) === String(microAgentId)
              );
              
              if (!microAgentData) {
                throw new Error('Micro agent not found in the response');
              }
              
              if (microAgentData.is_mini_agent) {
                setError('This is a mini-agent, not a micro-agent. Redirecting...');
                setTimeout(() => {
                  navigate(`/agents/${agentId}/mini-agents/${microAgentId}`);
                }, 2000);
                return;
              }
              
              const parentAgentName = parentAgentData?.agentName || parentAgentData?.repoName || 'AgentDeveloper';
              const normalizedMicroAgent = transformMicroAgentFromNew(microAgentData, parentAgentName);
              
              // Cache micro agent for 5 minutes
              cacheService.set(microAgentCacheKey, normalizedMicroAgent, CACHE_TTL.MEDIUM);
              
              setMicroAgent(normalizedMicroAgent);
              
              // Fetch config files if we have agent number
              if (normalizedMicroAgent.agentNumber) {
                fetchMicroConfigFiles(normalizedMicroAgent.agentNumber);
              }
            } else {
              throw new Error('No micro-agents found in API response');
            }
          }
        } else {
          console.error('Micro agent fetch failed:', microAgentResult.reason);
          setError('Failed to load micro agent details. Please try again.');
        }

        // Process prompts result (non-critical)
        if (promptsResult.status === 'fulfilled') {
          const promptsResponse = promptsResult.value;
          const promptsCacheKey = `prompts_${agentId}_${microAgentId}`;
          
          if (promptsResponse.fromCache) {
            console.log('📦 Using cached prompts');
            setPrompts(promptsResponse.prompts);
          } else if (promptsResponse && promptsResponse.prompts) {
            const promptsList = Object.keys(promptsResponse.prompts);
            setPrompts(promptsList);
            cacheService.set(promptsCacheKey, promptsList, CACHE_TTL.MEDIUM);
          } else if (Array.isArray(promptsResponse)) {
            setPrompts(promptsResponse);
            cacheService.set(promptsCacheKey, promptsResponse, CACHE_TTL.MEDIUM);
          } else {
            setPrompts(['system-prompt', 'user-prompt', 'assistant-prompt']);
          }
        } else {
          console.warn('Prompts fetch failed (non-critical), using defaults');
          setPrompts(['system-prompt', 'user-prompt', 'assistant-prompt']);
        }

        // Process environment functions result (non-critical)
        if (environmentFunctionsResult.status === 'fulfilled') {
          const envResponse = environmentFunctionsResult.value;
          const envFuncCacheKey = `env_functions_${agentId}_${microAgentId}`;
          
          if (envResponse.fromCache) {
            console.log('📦 Using cached environment functions');
            const functions = envResponse.environmentVariables;
            setEnvironmentFunctions(Array.isArray(functions) ? functions : Object.keys(functions));
          } else if (envResponse && envResponse.environmentVariables) {
            const functions = Object.keys(envResponse.environmentVariables);
            setEnvironmentFunctions(functions);
            cacheService.set(envFuncCacheKey, functions, CACHE_TTL.MEDIUM);
          } else if (Array.isArray(envResponse)) {
            setEnvironmentFunctions(envResponse);
            cacheService.set(envFuncCacheKey, envResponse, CACHE_TTL.MEDIUM);
          } else {
            setEnvironmentFunctions(['initialize', 'process', 'finalize', 'get-logs', 'web-interaction']);
          }
        } else {
          console.warn('Environment functions fetch failed (non-critical), using defaults');
          setEnvironmentFunctions(['initialize', 'process', 'finalize', 'get-logs', 'web-interaction']);
        }
        
        setInitialLoadComplete(true);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching microAgent details:', error);
        setError('Failed to load micro agent details. Please try again.');
        setLoading(false);
      }
    };
    
    fetchMicroAgentDetails();
  }, [agentId, microAgentId, navigate]);

  const fetchMicroConfigFiles = async (agentNumber) => {
    try {
      console.log(`Fetching micro-config files for agent number: ${agentNumber}`);
      
      // Check cache first
      const configCacheKey = `micro_config_${agentNumber}`;
      const cachedConfig = cacheService.get(configCacheKey);
      
      if (cachedConfig) {
        console.log('📦 Using cached micro config');
        setMicroConfigData(cachedConfig.config || {});
        setMetadataContent(cachedConfig.metadata || {});
        setConfigFiles(cachedConfig.files || ['config.json', 'settings.json', 'metadata.json']);
        return;
      }
      
      let parentAgentData = parentAgent;
      if (!parentAgentData) {
        try {
          const agentsResponse = await api.getAgents();
          if (agentsResponse && agentsResponse.success && agentsResponse.agents) {
            parentAgentData = agentsResponse.agents.find(a => 
              a.uniqueIdentifier === agentId || 
              a.agentName === agentId || 
              a.repoName === agentId
            );
          }
        } catch (err) {
          console.error('Error fetching parent agent for config:', err);
        }
      }
      
      const payload = {
        awsRegion: parentAgentData?.awsRegion || parentAgent?.awsRegion || "us-west-2",
        githubAccount: parentAgentData?.repoAccount || parentAgent?.repoAccount || "materassi",
        repository: parentAgentData?.repoName || parentAgent?.repoName || "AgentDeveloper",
        githubSecretName: "GitHubToken",
        awsAccount: parentAgentData?.awsAccountNumber || parentAgent?.awsAccount || "851725399969",
        agentNumber: agentNumber,
        parentAgentName: parentAgentData?.agentName || parentAgentData?.repoName || parentAgent?.name || "AgentDeveloper",
        isMiniAgent: false
      };
      
      const response = await api.makeAuthenticatedRequest('POST', `/micro-config/${agentNumber}`, payload);
      
      let config = extractFromResponse(response, 'config');
      let metadata = extractFromResponse(response, 'metadata');
      
      if (metadata) {
        setMetadataContent(metadata);
      }
      
      if (config) {
        setMicroConfigData(config);
        
        const fileNames = ['config.json', 'metadata.json'];
        
        if (typeof config === 'object') {
          Object.keys(config).forEach(key => {
            if (
              typeof config[key] === 'object' && 
              !Array.isArray(config[key]) && 
              key !== 'micro_agents' && 
              key !== 'settings' &&
              key !== 'metadata' &&
              !fileNames.includes(`${key}.json`)
            ) {
              fileNames.push(`${key}.json`);
            }
          });
          
          if (config.settings && !fileNames.includes('settings.json')) {
            fileNames.push('settings.json');
          }
        }
        
        if (fileNames.length === 0) {
          fileNames.push('config.json', 'settings.json', 'metadata.json');
        }
        
        setConfigFiles(fileNames);
        
        // Cache config for 5 minutes
        cacheService.set(configCacheKey, {
          config: config,
          metadata: metadata,
          files: fileNames
        }, CACHE_TTL.MEDIUM);
      } else {
        setMicroConfigData({});
        setMetadataContent({});
        setConfigFiles(['config.json', 'settings.json', 'metadata.json']);
      }
    } catch (err) {
      console.error('Error fetching micro-config files:', err);
      setMicroConfigData({});
      setMetadataContent({});
      setConfigFiles(['config.json', 'settings.json', 'metadata.json']);
    }
  };

  useEffect(() => {
    if (!selectedPrompt) return;

    const fetchSelectedPromptContent = async () => {
      if (!selectedPrompt) return;
      
      try {
        setIsSaving(true);
        setShowPromptsEditor(true);
        
        // Check cache first
        const promptCacheKey = `prompt_content_${agentId}_${microAgentId}_${selectedPrompt}`;
        const cachedPromptContent = cacheService.get(promptCacheKey);
        
        if (cachedPromptContent) {
          console.log('📦 Using cached prompt content');
          setPromptContent(cachedPromptContent);
          setIsSaving(false);
          return;
        }
        
        console.log(`Fetching content for prompt: ${selectedPrompt}`);
        
        const response = await api.getPromptContent(agentId, 'micro-agent', microAgentId, selectedPrompt);
        
        let content = '';
        if (typeof response === 'string') {
          content = response;
        } else if (response && response.content) {
          content = response.content;
        } else if (response && response.prompts && response.prompts[selectedPrompt]) {
          content = response.prompts[selectedPrompt];
        }
        
        setPromptContent(content);
        
        // Cache prompt content for 2 minutes (shorter TTL for editable content)
        cacheService.set(promptCacheKey, content, CACHE_TTL.SHORT);
        
        setIsSaving(false);
      } catch (error) {
        console.error('Error fetching prompt content:', error);
        toast.error('Failed to load prompt content');
        setPromptContent('');
        setIsSaving(false);
      }
    };

    fetchSelectedPromptContent();
  }, [agentId, microAgentId, selectedPrompt]);

  useEffect(() => {
    if (!selectedFunction) return;

    const fetchEnvironmentVariables = async () => {
      if (!selectedFunction) return;

      try {
        setIsSaving(true);
        setShowVarsEditor(true);
        
        // Check cache first
        const envVarCacheKey = `env_vars_${agentId}_${microAgentId}_${selectedFunction}`;
        const cachedEnvVars = cacheService.get(envVarCacheKey);
        
        if (cachedEnvVars) {
          console.log('📦 Using cached environment variables');
          setEnvironmentVariables(cachedEnvVars);
          setIsSaving(false);
          return;
        }
        
        console.log(`Fetching environment variables for function: ${selectedFunction}`);
        
        const response = await api.getEnvironmentVariables(agentId, 'micro-agent', microAgentId, selectedFunction);
        
        let vars = {};
        if (response && response.environmentVariables && response.environmentVariables[selectedFunction]) {
          vars = response.environmentVariables[selectedFunction];
        } else if (response && typeof response === 'object') {
          vars = response;
        }
        
        setEnvironmentVariables(vars);
        
        // Cache environment variables for 2 minutes
        cacheService.set(envVarCacheKey, vars, CACHE_TTL.SHORT);
        
        setIsSaving(false);
      } catch (error) {
        console.error('Error fetching environment variables:', error);
        toast.error('Failed to load environment variables');
        setEnvironmentVariables({});
        setIsSaving(false);
      }
    };
    
    fetchEnvironmentVariables();
  }, [agentId, microAgentId, selectedFunction]);

  useEffect(() => {
    if (!selectedConfigFile || !microAgent?.agentNumber) return;

    const fetchConfigFileContent = async () => {
      try {
        setIsSaving(true);
        setShowConfigEditor(true);
        
        const agentNumber = microAgent.agentNumber;
        let configData = null;
        
        console.log(`Fetching config file content for: ${selectedConfigFile}`);
        
        if (selectedConfigFile === 'config.json') {
          configData = microConfigData;
          if (configData && configData.metadata) {
            configData = {...configData};
            delete configData.metadata;
          }
        } else if (selectedConfigFile === 'metadata.json') {
          configData = metadataContent && Object.keys(metadataContent).length > 0 
            ? metadataContent 
            : (microConfigData && microConfigData.metadata ? microConfigData.metadata : null);
        } else {
          const configKey = selectedConfigFile.replace('.json', '');
          
          if (microConfigData && microConfigData[configKey]) {
            configData = microConfigData[configKey];
          } else {
            try {
              const payload = {
                awsRegion: parentAgent?.awsRegion || "us-west-2",
                githubAccount: parentAgent?.repoAccount || "materassi",
                repository: parentAgent?.repoName || "AgentDeveloper",
                githubSecretName: "GitHubToken",
                awsAccount: parentAgent?.awsAccount || "851725399969",
                agentNumber: agentNumber,
                fileName: selectedConfigFile,
                parentAgentName: parentAgent?.name || "AgentDeveloper",
                isMiniAgent: false
              };
              
              const response = await api.makeAuthenticatedRequest('POST', `/micro-config/${agentNumber}`, payload);
              configData = extractFromResponse(response, configKey) || extractFromResponse(response, 'config');
            } catch (fetchErr) {
              console.warn('Could not fetch config from API:', fetchErr);
              configData = null;
            }
          }
        }
        
        if (configData) {
          setConfigContent(JSON.stringify(configData, null, 2));
        } else {
          let defaultContent = {};
          if (selectedConfigFile === 'settings.json') {
            defaultContent = { timeout: 30000, retries: 3, maxTokens: 1000 };
          } else if (selectedConfigFile === 'metadata.json') {
            defaultContent = { version: "1.0.0", author: "Agent Developer", lastUpdated: new Date().toISOString() };
          } else {
            defaultContent = { enabled: true, version: "1.0.0" };
          }
          
          setConfigContent(JSON.stringify(defaultContent, null, 2));
        }
        
        setIsSaving(false);
      } catch (error) {
        console.error('Error fetching config file content:', error);
        toast.error('Failed to load configuration file');
        
        let defaultContent = {};
        if (selectedConfigFile === 'settings.json') {
          defaultContent = { timeout: 30000, retries: 3, maxTokens: 1000 };
        } else if (selectedConfigFile === 'metadata.json') {
          defaultContent = { version: "1.0.0", author: "Agent Developer", lastUpdated: new Date().toISOString() };
        } else {
          defaultContent = { enabled: true, version: "1.0.0" };
        }
        
        setConfigContent(JSON.stringify(defaultContent, null, 2));
        setIsSaving(false);
      }
    };

    fetchConfigFileContent();
  }, [microAgent, selectedConfigFile, microConfigData, agentId, metadataContent, parentAgent]);

  // OPTIMIZATION 5: LAZY LOAD LAMBDA FUNCTIONS (only when tab is active)
  useEffect(() => {
    if (!microAgent?.agentNumber || activeTab !== 'lambda') return;

    const fetchLambdaFunctions = async () => {
      try {
        setLoadingLambda(true);
        
        // Check cache first
        const lambdaCacheKey = `lambda_functions_${microAgent.agentNumber}`;
        const cachedLambda = cacheService.get(lambdaCacheKey);
        
        if (cachedLambda) {
          console.log('📦 Using cached lambda functions');
          setLambdaFunctions(cachedLambda);
          setLoadingLambda(false);
          return;
        }
        
        const payload = {
          parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
          repository: parentAgent?.repoName || "AgentDeveloper",
          githubAccount: parentAgent?.repoAccount || "materassi",
          awsRegion: parentAgent?.awsRegion || "us-west-2",
          agentNumber: microAgent.agentNumber
        };
        
        console.log('Fetching lambda functions with payload:', payload);
        
        const response = await api.makeAuthenticatedRequest('POST', '/lambda', payload);
        
        const lambdaFuncs = extractFromResponse(response, 'lambdaFunctions');
        
        if (lambdaFuncs && typeof lambdaFuncs === 'object') {
          setLambdaFunctions(lambdaFuncs);
          // Cache lambda functions for 5 minutes
          cacheService.set(lambdaCacheKey, lambdaFuncs, CACHE_TTL.MEDIUM);
          console.log('Lambda functions loaded successfully:', Object.keys(lambdaFuncs).length);
        } else {
          console.warn('No lambda functions found in response');
          setLambdaFunctions({});
        }
        
        setLoadingLambda(false);
      } catch (error) {
        console.error('Error fetching lambda functions:', error);
        toast.error('Failed to load lambda functions');
        setLambdaFunctions({});
        setLoadingLambda(false);
      }
    };
    
    fetchLambdaFunctions();
  }, [microAgent, parentAgent, activeTab]);

  useEffect(() => {
    if (!selectedLambdaFunction || !lambdaFunctions[selectedLambdaFunction]) return;
    
    setLambdaFunctionCode(lambdaFunctions[selectedLambdaFunction].code || '');
    setShowLambdaEditor(true);
  }, [selectedLambdaFunction, lambdaFunctions]);

  // OPTIMIZATION 5: LAZY LOAD STATE MACHINE (only when tab is active)
  useEffect(() => {
    if (!microAgent?.agentNumber || activeTab !== 'stepfunction') return;

    const fetchStateMachine = async () => {
      try {
        setLoadingStateMachine(true);
        
        // Check cache first
        const smCacheKey = `state_machine_${microAgent.agentNumber}_${selectedEnvironment}`;
        const cachedSM = cacheService.get(smCacheKey);
        
        if (cachedSM) {
          console.log('📦 Using cached state machine');
          setStateMachine(cachedSM.stateMachine);
          setMermaidDiagram(cachedSM.mermaidDiagram);
          setLoadingStateMachine(false);
          return;
        }
        
        const payload = {
          parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
          repository: parentAgent?.repoName || "AgentDeveloper",
          githubAccount: parentAgent?.repoAccount || "materassi",
          awsRegion: parentAgent?.awsRegion || "us-west-2",
          agentNumber: microAgent.agentNumber,
          environment: selectedEnvironment
        };
        
        console.log('Fetching state machine with payload:', payload);
        
        const response = await api.makeAuthenticatedRequest('POST', '/stepfunction', payload);
        
        const stateMachineData = extractFromResponse(response, 'stateMachine');
        
        if (stateMachineData) {
          setStateMachine(stateMachineData);
          
          const mermaid = generateMermaidDiagram(stateMachineData.definition);
          setMermaidDiagram(mermaid);
          
          // Cache state machine for 5 minutes
          cacheService.set(smCacheKey, {
            stateMachine: stateMachineData,
            mermaidDiagram: mermaid
          }, CACHE_TTL.MEDIUM);
          
          console.log('State machine loaded successfully');
        } else {
          console.warn('No state machine found in response');
          setStateMachine(null);
          setMermaidDiagram('');
        }
        
        setLoadingStateMachine(false);
      } catch (error) {
        console.error('Error fetching state machine:', error);
        toast.error('Failed to load state machine');
        setStateMachine(null);
        setMermaidDiagram('');
        setLoadingStateMachine(false);
      }
    };
    
    fetchStateMachine();
  }, [microAgent, parentAgent, activeTab, selectedEnvironment]);

  const generateMermaidDiagram = (definition) => {
    if (!definition || !definition.States) {
      return '';
    }
    
    let diagram = 'graph TD;\\\\\\\\n';
    const states = definition.States;
    const startAt = definition.StartAt;
    
    diagram += `  Start((Start)) --> ${startAt};\\\\\\\\n`;
    
    Object.entries(states).forEach(([stateName, stateConfig]) => {
      if (stateConfig.Type === 'Task') {
        let functionName = "Task";
        if (stateConfig.Parameters && stateConfig.Parameters.FunctionName) {
          const arnParts = stateConfig.Parameters.FunctionName.split(':');
          functionName = arnParts[arnParts.length - 1];
          if (functionName.length > 40) {
            functionName = functionName.substring(0, 37) + '...';
          }
        }
        
        diagram += `  ${stateName}[${stateName}<br/>${functionName}]`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
        
        if (stateConfig.Catch) {
          stateConfig.Catch.forEach((catchBlock, index) => {
            if (catchBlock.Next) {
              diagram += `  ${stateName} -->|Error ${index+1}| ${catchBlock.Next};\\\\\\\\n`;
            }
          });
        }
        
        if (stateConfig.Retry && stateConfig.Retry.length > 0) {
          diagram += `  ${stateName} -->|Retry| ${stateName};\\\\\\\\n`;
        }
      } else if (stateConfig.Type === 'Choice') {
        if (stateConfig.Choices) {
          stateConfig.Choices.forEach((choice, index) => {
            if (choice.Next) {
              diagram += `  ${stateName}{${stateName}} -->|Choice ${index+1}| ${choice.Next};\\\\\\\\n`;
            }
          });
          
          if (stateConfig.Default) {
            diagram += `  ${stateName} -->|Default| ${stateConfig.Default};\\\\\\\\n`;
          }
        }
      } else if (stateConfig.Type === 'Parallel') {
        diagram += `  ${stateName}{{${stateName}}}`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
        
        if (stateConfig.Branches && stateConfig.Branches.length > 0) {
          stateConfig.Branches.forEach((branch, index) => {
            if (branch.StartAt) {
              diagram += `  ${stateName} -->|Branch ${index+1}| ${branch.StartAt};\\\\\\\\n`;
            }
          });
        }
      } else if (stateConfig.Type === 'Map') {
        diagram += `  ${stateName}[/${stateName}/]`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
      } else if (stateConfig.Type === 'Succeed') {
        diagram += `  ${stateName}((${stateName})) --> End((End));\\\\\\\\n`;
      } else if (stateConfig.Type === 'Fail') {
        diagram += `  ${stateName}[/${stateName}/] --> End((End));\\\\\\\\n`;
      } else if (stateConfig.Type === 'Wait') {
        diagram += `  ${stateName}[${stateName}]`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
      } else {
        diagram += `  ${stateName}[${stateName}]`;
        
        if (stateConfig.Next) {
          diagram += ` --> ${stateConfig.Next};\\\\\\\\n`;
        } else {
          diagram += `;\\\\\\\\n`;
        }
      }
    });
    
    return diagram;
  };

  const handlePromptSelect = (promptName) => {
    setSelectedPrompt(promptName);
    setShowPromptsEditor(true);
    setActiveTab('prompts');
  };

  const handleFunctionSelect = (functionName) => {
    setSelectedFunction(functionName);
    setShowVarsEditor(true);
    setActiveTab('environment');
  };

  const handleConfigFileSelect = (fileName) => {
    setSelectedConfigFile(fileName);
    setShowConfigEditor(true);
    setActiveTab('config');
  };

  const handleLambdaFunctionSelect = (functionName) => {
    setSelectedLambdaFunction(functionName);
    setShowLambdaEditor(true);
    setActiveTab('lambda');
  };

  const handlePromptContentChange = (e) => {
    setPromptContent(e.target.value);
  };

  const handleEnvironmentVariableChange = (key, value) => {
    setEnvironmentVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAddEnvironmentVariable = () => {
    setEnvironmentVariables(prev => ({
      ...prev,
      '': ''
    }));
  };

  const handleRemoveEnvironmentVariable = (key) => {
    setEnvironmentVariables(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleConfigContentChange = (e) => {
    setConfigContent(e.target.value);
  };

  const handleEnvironmentChange = (env) => {
    setSelectedEnvironment(env);
  };

  const handleSavePrompt = async () => {
    if (!selectedPrompt) return;

    try {
      setIsSaving(true);
      
      const agentNumber = microAgent.agentNumber;
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error(`Invalid agent number: ${agentNumber}. Must be a 3-digit number.`);
      }
      
      console.log(`Saving prompt '${selectedPrompt}' for agent number ${agentNumber}`);
      
      const payload = {
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        githubAccount: parentAgent?.repoAccount || "materassi",
        repository: parentAgent?.repoName || "AgentDeveloper",
        githubSecretName: "GitHubToken",
        agentNumber: agentNumber,
        parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
        files: {
          [selectedPrompt]: promptContent
        }
      };
      
      const response = await api.makeAuthenticatedRequest('PUT', `/prompts/${agentNumber}`, payload);
      
      const updateResults = extractFromResponse(response, 'updateResults');
      
      if (updateResults && Array.isArray(updateResults)) {
        const promptResult = updateResults.find(r => r.prompt_name === selectedPrompt);
        
        if (promptResult) {
          const githubSuccess = promptResult.github_update?.success;
          const devS3Success = promptResult.s3_update1?.success;
          const prodS3Success = promptResult.s3_update2?.success;
          
          if (githubSuccess && (devS3Success || prodS3Success)) {
            toast.success(`Prompt "${selectedPrompt}" saved successfully!`);
          } else if (githubSuccess) {
            toast.success(`Prompt "${selectedPrompt}" saved to GitHub. Note: S3 bucket updates may require deployment first.`);
          } else if (devS3Success || prodS3Success) {
            toast.success(`Prompt "${selectedPrompt}" saved to S3, but GitHub update failed.`);
          } else {
            throw new Error('Failed to save prompt to any location');
          }
        } else {
          toast.success(`Prompt "${selectedPrompt}" update processed, but no specific results returned.`);
        }
        
        // Invalidate cache after save
        const promptCacheKey = `prompt_content_${agentId}_${microAgentId}_${selectedPrompt}`;
        cacheService.remove(promptCacheKey);
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response && response.success) {
        toast.success(`Prompt "${selectedPrompt}" saved successfully!`);
        
        // Invalidate cache after save
        const promptCacheKey = `prompt_content_${agentId}_${microAgentId}_${selectedPrompt}`;
        cacheService.remove(promptCacheKey);
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        throw new Error('Unrecognized response format when saving prompt');
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast.error('Failed to save prompt content: ' + (error.message || 'Unknown error'));
      setIsSaving(false);
    }
  };

  const handleSaveEnvironmentVariables = async () => {
    if (!selectedFunction) return;

    try {
      setIsSaving(true);
      
      const agentNumber = microAgent.agentNumber;
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error(`Invalid agent number: ${agentNumber}. Must be a 3-digit number.`);
      }
      
      console.log(`Saving environment variables for function '${selectedFunction}' for agent number ${agentNumber}`);
      
      const payload = {
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        githubAccount: parentAgent?.repoAccount || "materassi",
        repository: parentAgent?.repoName || "AgentDeveloper",
        githubSecretName: "GitHubToken",
        agentNumber: agentNumber,
        parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
        files: {
          [selectedFunction]: environmentVariables
        }
      };
      
      const response = await api.makeAuthenticatedRequest('PUT', `/environment/${agentNumber}`, payload);
      
      const updateResults = extractFromResponse(response, 'updateResults');
      
      if (updateResults && Array.isArray(updateResults)) {
        const functionResult = updateResults.find(r => r.function_name === selectedFunction);
        
        if (functionResult) {
          const githubSuccess = functionResult.github_update?.github_success;
          const anyAwsSuccess = functionResult.aws_updates?.some(u => u.aws_success);
          
          if (githubSuccess && anyAwsSuccess) {
            toast.success(`Environment variables for "${selectedFunction}" saved successfully!`);
          } else if (githubSuccess) {
            toast.success(`Environment variables for "${selectedFunction}" saved to GitHub. Some AWS Lambda updates may require deployment first.`);
          } else if (anyAwsSuccess) {
            toast.success(`Environment variables for "${selectedFunction}" updated in AWS, but GitHub update failed.`);
          } else {
            throw new Error('Failed to save environment variables to any location');
          }
        } else {
          toast.success(`Environment variables for "${selectedFunction}" update processed, but no specific results returned.`);
        }
        
        // Invalidate cache after save
        const envVarCacheKey = `env_vars_${agentId}_${microAgentId}_${selectedFunction}`;
        cacheService.remove(envVarCacheKey);
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response && response.success) {
        toast.success(`Environment variables for "${selectedFunction}" saved successfully!`);
        
        // Invalidate cache after save
        const envVarCacheKey = `env_vars_${agentId}_${microAgentId}_${selectedFunction}`;
        cacheService.remove(envVarCacheKey);
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        throw new Error('Unrecognized response format when saving environment variables');
      }
      
      setIsSaving(false);
    } catch (error) {
      console.error('Error saving environment variables:', error);
      toast.error('Failed to save environment variables: ' + (error.message || 'Unknown error'));
      setIsSaving(false);
    }
  };

  const handleSaveConfigFile = async () => {
    if (!selectedConfigFile || !microAgent?.agentNumber) return;

    try {
      setIsSaving(true);
      
      let configData;
      try {
        configData = JSON.parse(configContent);
      } catch (e) {
        toast.error('Invalid JSON in configuration file');
        setIsSaving(false);
        return;
      }
      
      const agentNumber = microAgent.agentNumber;
      if (!agentNumber || agentNumber.length !== 3) {
        console.error('Invalid agent number:', agentNumber);
        throw new Error(`Invalid agent number: ${agentNumber}. Must be a 3-digit number.`);
      }
      
      console.log(`Saving config file '${selectedConfigFile}' for agent number ${agentNumber}`);
      
      const payload = {
        agentNumber: agentNumber,
        parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
        repository: parentAgent?.repoName || "AgentDeveloper",
        githubAccount: parentAgent?.repoAccount || "materassi",
        awsRegion: parentAgent?.awsRegion || "us-west-2",
        githubSecretName: "GitHubToken",
        isMiniAgent: false
      };
      
      if (selectedConfigFile === 'config.json') {
        payload.fileType = "config";
        payload.config = configData;
      } else if (selectedConfigFile === 'metadata.json') {
        payload.fileType = "metadata";
        payload.metadata = configData;
      } else {
        const configKey = selectedConfigFile.replace('.json', '');
        payload.fileType = configKey;
        payload[configKey] = configData;
      }
      
      const response = await api.makeAuthenticatedRequest('PUT', `/micro-config/${agentNumber}`, payload);
      
      const updateResult = extractFromResponse(response, 'updateResult');
      
      if (updateResult) {
        const githubSuccess = updateResult.github_update?.success;
        const devS3Success = updateResult.s3_update1?.success;
        const prodS3Success = updateResult.s3_update2?.success;
        
        if (githubSuccess && (devS3Success || prodS3Success)) {
          toast.success(`Configuration "${selectedConfigFile}" saved successfully!`);
        } else if (githubSuccess) {
          toast.success(`Configuration "${selectedConfigFile}" saved to GitHub. Note: S3 bucket updates may require deployment first.`);
        } else if (devS3Success || prodS3Success) {
          toast.success(`Configuration "${selectedConfigFile}" saved to S3, but GitHub update failed.`);
        } else {
          throw new Error('Failed to save configuration to any location');
        }
        
        let updatedMicroConfigData = {...microConfigData};
        
        if (selectedConfigFile === 'config.json') {
          if (microConfigData.metadata && !configData.metadata) {
            configData.metadata = microConfigData.metadata;
          }
          setMicroConfigData(configData);
        } else if (selectedConfigFile === 'metadata.json') {
          updatedMicroConfigData.metadata = configData;
          setMicroConfigData(updatedMicroConfigData);
        } else {
          const configKey = selectedConfigFile.replace('.json', '');
          updatedMicroConfigData[configKey] = configData;
          setMicroConfigData(updatedMicroConfigData);
        }
        
        // Invalidate cache after save
        const configCacheKey = `micro_config_${agentNumber}`;
        cacheService.remove(configCacheKey);
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else if (response && response.success) {
        toast.success(`Configuration "${selectedConfigFile}" saved successfully!`);
        
        let updatedMicroConfigData = {...microConfigData};
        
        if (selectedConfigFile === 'config.json') {
          if (microConfigData.metadata && !configData.metadata) {
            configData.metadata = microConfigData.metadata;
          }
          setMicroConfigData(configData);
        } else if (selectedConfigFile === 'metadata.json') {
          updatedMicroConfigData.metadata = configData;
          setMicroConfigData(updatedMicroConfigData);
        } else {
          const configKey = selectedConfigFile.replace('.json', '');
          updatedMicroConfigData[configKey] = configData;
          setMicroConfigData(updatedMicroConfigData);
        }
        
        // Invalidate cache after save
        const configCacheKey = `micro_config_${agentNumber}`;
        cacheService.remove(configCacheKey);
        
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        const errorMessage = 
          response?.error || 
          response?.message || 
          'Failed to save configuration';
        throw new Error(errorMessage);
      }
      
    } catch (error) {
      console.error('Error saving config file:', error);
      toast.error('Failed to save configuration: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeployToDev = async () => {
    if (!microAgent?.agentNumber) return;
    
    try {
      setIsDeploying(prev => ({ ...prev, dev: true }));
      
      let cdkQualifier = "agentdev";
      
      if (parentAgent && parentAgent.cdkQualifier) {
        cdkQualifier = parentAgent.cdkQualifier;
        console.log('Using cdkQualifier from parent agent:', cdkQualifier);
      } else {
        try {
          const agentsResponse = await api.getAgents();
          if (agentsResponse && agentsResponse.agents) {
            const agent = agentsResponse.agents.find(a => 
              a.uniqueIdentifier === agentId || 
              a.agentName === agentId || 
              a.repoName === agentId
            );
            
            if (agent && agent.cdkQualifier) {
              cdkQualifier = agent.cdkQualifier;
              console.log('Fetched cdkQualifier from agents endpoint:', cdkQualifier);
            }
          }
        } catch (err) {
          console.warn('Error fetching cdkQualifier from agents endpoint:', err);
        }
      }
      
      const payload = {
        githubAccount: parentAgent?.repoAccount || "materassi",
        repository: parentAgent?.repoName || "AgentDeveloper",
        agentNumber: microAgent.agentNumber,
        deploymentType: "agent",
        cdkQualifier: cdkQualifier,
        environment: "dev"
      };
      
      const response = await api.makeAuthenticatedRequest('POST', '/deploy', payload);
      
      if (response && response.success) {
        toast.success('Deployment to development environment initiated successfully!');
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        throw new Error('Failed to initiate deployment');
      }
    } catch (error) {
      console.error('Error deploying to dev:', error);
      toast.error('Failed to deploy to development environment');
    } finally {
      setIsDeploying(prev => ({ ...prev, dev: false }));
    }
  };
  
  const handleDeployToProd = async () => {
    if (!microAgent?.agentNumber) return;
    
    try {
      setIsDeploying(prev => ({ ...prev, prod: true }));
      
      let cdkQualifier = "agentdev";
      
      if (parentAgent && parentAgent.cdkQualifier) {
        cdkQualifier = parentAgent.cdkQualifier;
        console.log('Using cdkQualifier from parent agent:', cdkQualifier);
      } else {
        try {
          const agentsResponse = await api.getAgents();
          if (agentsResponse && agentsResponse.agents) {
            const agent = agentsResponse.agents.find(a => 
              a.uniqueIdentifier === agentId || 
              a.agentName === agentId || 
              a.repoName === agentId
            );
            
            if (agent && agent.cdkQualifier) {
              cdkQualifier = agent.cdkQualifier;
              console.log('Fetched cdkQualifier from agents endpoint:', cdkQualifier);
            }
          }
        } catch (err) {
          console.warn('Error fetching cdkQualifier from agents endpoint:', err);
        }
      }
      
      const payload = {
        githubAccount: parentAgent?.repoAccount || "materassi",
        repository: parentAgent?.repoName || "AgentDeveloper",
        agentNumber: microAgent.agentNumber,
        deploymentType: "agent",
        cdkQualifier: cdkQualifier,
        environment: "prod"
      };
      
      const response = await api.makeAuthenticatedRequest('POST', '/deploy', payload);
      
      if (response && response.success) {
        toast.success('Deployment to production environment initiated successfully!');
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        throw new Error('Failed to initiate deployment');
      }
    } catch (error) {
      console.error('Error deploying to prod:', error);
      toast.error('Failed to deploy to production environment');
    } finally {
      setIsDeploying(prev => ({ ...prev, prod: false }));
    }
  };
  
  const handleGoBackToParent = () => {
    navigate(`/agents/${agentId}`);
  };

  const handleGoToTesting = () => {
    navigate(`/agents/${agentId}/micro-agents/${microAgentId}/testing`);
  };

  const handleGoToLogs = () => {
    navigate(`/agents/${agentId}/micro-agents/${microAgentId}/logs`);
  };

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    
    if (tabKey === 'environment') {
      setShowPromptsEditor(false);
      setSelectedPrompt(null);
      setShowLambdaEditor(false);
      setSelectedLambdaFunction(null);
      setShowConfigEditor(false);
      setSelectedConfigFile(null);
    } else if (tabKey === 'prompts') {
      setShowVarsEditor(false);
      setSelectedFunction(null);
      setShowLambdaEditor(false);
      setSelectedLambdaFunction(null);
      setShowConfigEditor(false);
      setSelectedConfigFile(null);
    } else if (tabKey === 'lambda') {
      setShowVarsEditor(false);
      setSelectedFunction(null);
      setShowPromptsEditor(false);
      setSelectedPrompt(null);
      setShowConfigEditor(false);
      setSelectedConfigFile(null);
    } else if (tabKey === 'config') {
      setShowVarsEditor(false);
      setSelectedFunction(null);
      setShowPromptsEditor(false);
      setSelectedPrompt(null);
      setShowLambdaEditor(false);
      setSelectedLambdaFunction(null);
    }
  };

  const clearSelectedItems = () => {
    if (activeTab === 'environment') {
      setSelectedFunction(null);
      setShowVarsEditor(false);
    } else if (activeTab === 'prompts') {
      setSelectedPrompt(null);
      setShowPromptsEditor(false);
    } else if (activeTab === 'lambda') {
      setSelectedLambdaFunction(null);
      setShowLambdaEditor(false);
    } else if (activeTab === 'config') {
      setSelectedConfigFile(null);
      setShowConfigEditor(false);
    }
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const isValidJson = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  // OPTIMIZATION 4: LOADING SKELETON
  if (loading) {
    return (
      <Container fluid className="micro-agent-dev-container">
        <div className="loading-skeleton">
          <div className="skeleton-header">
            <div className="skeleton-title"></div>
            <div className="skeleton-description"></div>
            <div className="skeleton-meta">
              <div className="skeleton-meta-item"></div>
              <div className="skeleton-meta-item"></div>
              <div className="skeleton-meta-item"></div>
            </div>
          </div>
          <div className="skeleton-tabs">
            <div className="skeleton-tab"></div>
            <div className="skeleton-tab"></div>
            <div className="skeleton-tab"></div>
            <div className="skeleton-tab"></div>
          </div>
          <div className="skeleton-content">
            <div className="skeleton-sidebar">
              <div className="skeleton-list-item"></div>
              <div className="skeleton-list-item"></div>
              <div className="skeleton-list-item"></div>
              <div className="skeleton-list-item"></div>
              <div className="skeleton-list-item"></div>
            </div>
          </div>
        </div>
        <p className="text-center mt-3" style={{ color: 'var(--cool-grey)' }}>Loading micro agent details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="micro-agent-dev-container">
        <div className="error-container">
          <h4 className="error-heading">Error Loading Micro Agent</h4>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleGoBackToParent}>
              <FaArrowLeft className="me-2" />
              Back to Parent Agent
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="micro-agent-dev-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="agent-info-header">
        <div className="agent-title-container">
          <h1 className="agent-title">
            {microAgent?.name ? capitalizeFirstLetter(microAgent.name) : 'Unnamed Agent'}
          </h1>
          <p className="agent-description">{microAgent?.description || 'No description available'}</p>
        </div>
        
        <div className="agent-meta-container">
          <div className="agent-meta-row">
            <div className="meta-item">
              <span className="meta-label">Parent Agent:</span>
              <span className="meta-value">{parentAgent?.name || microAgent?.parentAgentName}</span>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Agent Number:</span>
              <span className="meta-value">
                <span className="agent-number-badge">{microAgent?.agentNumber || '000'}</span>
              </span>
            </div>
            
            <div className="meta-item">
              <span className="meta-label">Status:</span>
              <span className="meta-value">
                {microAgent?.commissioned ? 'Commissioned' : 'Not Commissioned'}
              </span>
            </div>
          </div>
          
          <div className="deployment-status-row">
            <div className="deployment-status">
              <span className="status-label">Development:</span>
              <Badge bg={microAgent?.devStatus === 'deployed' ? 'success' : 'secondary'} className="status-badge">
                {microAgent?.devStatus === 'deployed' ? 'Deployed' : 'Not Deployed'}
              </Badge>
              <Button 
                variant="primary" 
                size="sm" 
                className="deploy-button"
                onClick={handleDeployToDev}
                disabled={isDeploying.dev}
              >
                {isDeploying.dev ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <FaServer className="me-1" /> Deploy to Dev
                  </>
                )}
              </Button>
            </div>
            
            <div className="deployment-status">
              <span className="status-label">Production:</span>
              <Badge bg={microAgent?.prodStatus === 'deployed' ? 'success' : 'secondary'} className="status-badge">
                {microAgent?.prodStatus === 'deployed' ? 'Deployed' : 'Not Deployed'}
              </Badge>
              <Button 
                variant="danger" 
                size="sm" 
                className="deploy-button"
                onClick={handleDeployToProd}
                disabled={isDeploying.prod}
              >
                {isDeploying.prod ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <FaServer className="me-1" /> Deploy to Prod
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        
        <div className="action-buttons-container">
          <Button variant="outline-light" onClick={handleGoBackToParent} className="action-button">
            <FaArrowLeft className="me-2" />
            Back to Parent Agent
          </Button>
          
          <Button variant="outline-light" onClick={handleGoToTesting} className="action-button">
            <FaVial className="me-2" />
            Test Agent
          </Button>
          
          <Button variant="outline-light" onClick={handleGoToLogs} className="action-button">
            <FaHistory className="me-2" />
            View Logs
          </Button>
        </div>
      </div>

      <div className="content-container">
        <Nav variant="tabs" activeKey={activeTab} onSelect={handleTabChange} className="main-tabs">
          <Nav.Item>
            <Nav.Link eventKey="prompts">Prompts</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="environment">Environment Variables</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="config">Configuration</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="lambda">Lambda Functions</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="stepfunction">Step Function</Nav.Link>
          </Nav.Item>
        </Nav>

        
        {activeTab === 'prompts' && (
          <div className="tab-content-container">
            <div className={`sidebar ${showPromptsEditor ? 'with-editor' : 'full-width'}`}>
              <div className="sidebar-header">
                <h3><FaFileAlt className="me-2" />Prompt Files</h3>
              </div>
              <div className="prompt-list">
                {prompts.map((prompt) => (
                  <div
                    key={prompt}
                    className={`prompt-item ${selectedPrompt === prompt ? 'active' : ''}`}
                    onClick={() => handlePromptSelect(prompt)}
                  >
                    <div className="prompt-name">{prompt}</div>
                  </div>
                ))}
              </div>
              <div className="sidebar-help-text">
                Select a prompt file to view or edit it
              </div>
            </div>
            
            {showPromptsEditor && selectedPrompt && (
              <div className="editor-container">
                <div className="editor-header">
                  <h3>Editing Prompt: {selectedPrompt}</h3>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={clearSelectedItems}
                    className="close-editor-btn"
                  >
                    <FaTimes />
                  </Button>
                </div>
                
                <div className="editor-content">
                  {isSaving ? (
                    <div className="loading-container py-4">
                      <Spinner />
                      <p>Loading prompt content...</p>
                    </div>
                  ) : (
                    <Form.Control
                      as="textarea"
                      value={promptContent}
                      onChange={handlePromptContentChange}
                      className="prompt-editor"
                      rows={20}
                    />
                  )}
                </div>
                
                <div className="editor-footer">
                  <Button 
                    variant="primary" 
                    onClick={handleSavePrompt}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Prompt
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'environment' && (
          <div className="tab-content-container">
            <div className={`sidebar ${showVarsEditor ? 'with-editor' : 'full-width'}`}>
              <div className="sidebar-header">
                <h3><FaCode className="me-2" />Functions</h3>
              </div>
              <div className="function-list">
                {environmentFunctions.map((func) => (
                  <div
                    key={func}
                    className={`function-item ${selectedFunction === func ? 'active' : ''}`}
                    onClick={() => handleFunctionSelect(func)}
                  >
                    <div className="function-name">{func}</div>
                  </div>
                ))}
              </div>
              <div className="sidebar-help-text">
                Select a function to view or edit its environment variables
              </div>
            </div>
            
            {showVarsEditor && selectedFunction && (
              <div className="editor-container">
                <div className="editor-header">
                  <h3>Environment Variables: {selectedFunction}</h3>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={clearSelectedItems}
                    className="close-editor-btn"
                  >
                    <FaTimes />
                  </Button>
                </div>
                
                <div className="editor-content">
                  <div className="toolbar mb-3">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={handleAddEnvironmentVariable}
                      className="add-variable-btn"
                    >
                      <FaPlus className="me-1" /> Add Variable
                    </Button>
                  </div>
                  
                  {isSaving ? (
                    <div className="loading-container py-4">
                      <Spinner />
                      <p>Loading variables...</p>
                    </div>
                  ) : Object.keys(environmentVariables).length > 0 ? (
                    <div className="variables-table-container">
                      <Table bordered className="variables-table">
                        <thead>
                          <tr>
                            <th width="40%">Key</th>
                            <th width="50%">Value</th>
                            <th width="10%">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(environmentVariables).map(([key, value], index) => (
                            <tr key={index}>
                              <td>
                                <Form.Control
                                  type="text"
                                  value={key}
                                  onChange={(e) => {
                                    const newKey = e.target.value;
                                    const newVariables = { ...environmentVariables };
                                    delete newVariables[key];
                                    newVariables[newKey] = value;
                                    setEnvironmentVariables(newVariables);
                                  }}
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="text"
                                  value={value}
                                  onChange={(e) => handleEnvironmentVariableChange(key, e.target.value)}
                                />
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleRemoveEnvironmentVariable(key)}
                                >
                                  <FaTimes />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>No environment variables defined for this function.</p>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddEnvironmentVariable}
                      >
                        <FaPlus className="me-1" /> Add First Variable
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="editor-footer">
                  <Button 
                    variant="primary" 
                    onClick={handleSaveEnvironmentVariables}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Environment Variables
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'config' && (
          <div className="tab-content-container">
            <div className={`sidebar ${showConfigEditor ? 'with-editor' : 'full-width'}`}>
              <div className="sidebar-header">
                <h3><FaCogs className="me-2" />Configuration Files</h3>
              </div>
              <div className="prompt-list">
                {configFiles.map((file) => (
                  <div
                    key={file}
                    className={`prompt-item ${selectedConfigFile === file ? 'active' : ''}`}
                    onClick={() => handleConfigFileSelect(file)}
                  >
                    <div className="prompt-name">{file}</div>
                  </div>
                ))}
              </div>
              <div className="sidebar-help-text">
                Select a configuration file to view or edit it
              </div>
            </div>
            
            {showConfigEditor && selectedConfigFile && (
              <div className="editor-container">
                <div className="editor-header">
                  <h3>Editing Config: {selectedConfigFile}</h3>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={clearSelectedItems}
                    className="close-editor-btn"
                  >
                    <FaTimes />
                  </Button>
                </div>
                
                <div className="editor-content">
                  {isSaving ? (
                    <div className="loading-container py-4">
                      <Spinner />
                      <p>Loading configuration content...</p>
                    </div>
                  ) : (
                    <>
                      <Form.Control
                        as="textarea"
                        value={configContent}
                        onChange={handleConfigContentChange}
                        className="prompt-editor"
                        rows={20}
                        isInvalid={configContent && !isValidJson(configContent)}
                      />
                      {configContent && !isValidJson(configContent) && (
                        <Form.Text className="text-danger mt-2">
                          Invalid JSON format. Configuration must be valid JSON.
                        </Form.Text>
                      )}
                    </>
                  )}
                </div>
                
                <div className="editor-footer">
                  <Button 
                    variant="primary" 
                    onClick={handleSaveConfigFile}
                    disabled={isSaving || (configContent && !isValidJson(configContent))}
                  >
                    {isSaving ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Save Configuration
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'lambda' && (
          <div className="tab-content-container">
            <div className={`sidebar ${showLambdaEditor ? 'with-editor' : 'full-width'}`}>
              <div className="sidebar-header">
                <h3><FaCode className="me-2" />Lambda Functions</h3>
              </div>
              <div className="function-list">
                {loadingLambda ? (
                  <div className="p-4 text-center">
                    <Spinner size="sm" />
                    <p className="mt-2">Loading lambda functions...</p>
                  </div>
                ) : Object.keys(lambdaFunctions).length > 0 ? (
                  Object.keys(lambdaFunctions).map((func) => (
                    <div
                      key={func}
                      className={`function-item ${selectedLambdaFunction === func ? 'active' : ''}`}
                      onClick={() => handleLambdaFunctionSelect(func)}
                    >
                      <div className="function-name">{func}</div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center">
                    <p>No lambda functions found.</p>
                  </div>
                )}
              </div>
              <div className="sidebar-help-text">
                Select a lambda function to view its code
              </div>
            </div>
            
            {showLambdaEditor && selectedLambdaFunction && (
              <div className="editor-container">
                <div className="editor-header">
                  <h3>Lambda Function: {selectedLambdaFunction}</h3>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    onClick={clearSelectedItems}
                    className="close-editor-btn"
                  >
                    <FaTimes />
                  </Button>
                </div>
                
                <div className="editor-content">
                  {loadingLambda ? (
                    <div className="loading-container py-4">
                      <Spinner />
                      <p>Loading lambda function code...</p>
                    </div>
                  ) : (
                    <>
                      <div className="file-path mb-2 text-muted">
                        <small>
                          {lambdaFunctions[selectedLambdaFunction]?.path || 'Path not available'}
                        </small>
                      </div>
                      <Form.Control
                        as="textarea"
                        value={lambdaFunctionCode}
                        className="prompt-editor"
                        rows={25}
                        readOnly
                      />
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'stepfunction' && (
          <div className="p-4">
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Step Function</h3>
                <div className="d-flex align-items-center">
                  <Form.Select 
                    value={selectedEnvironment}
                    onChange={(e) => handleEnvironmentChange(e.target.value)}
                    style={{ width: '150px' }}
                    className="me-3"
                  >
                    <option value="DEV">Development</option>
                    <option value="PROD">Production</option>
                  </Form.Select>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => {
                      setStateMachine(null);
                      setTimeout(() => {
                        const payload = {
                          parentAgentName: parentAgent?.name || parentAgent?.repoName || "AgentDeveloper",
                          repository: parentAgent?.repoName || "AgentDeveloper",
                          githubAccount: parentAgent?.repoAccount || "materassi",
                          awsRegion: parentAgent?.awsRegion || "us-west-2",
                          agentNumber: microAgent.agentNumber,
                          environment: selectedEnvironment
                        };
                        api.makeAuthenticatedRequest('POST', '/stepfunction', payload)
                          .then(response => {
                            const stateMachineData = extractFromResponse(response, 'stateMachine');
                            if (stateMachineData) {
                              setStateMachine(stateMachineData);
                              const mermaid = generateMermaidDiagram(stateMachineData.definition);
                              setMermaidDiagram(mermaid);
                            }
                          })
                          .catch(error => {
                            console.error('Error refreshing state machine:', error);
                            toast.error('Failed to refresh state machine');
                          });
                      }, 100);
                    }}
                  >
                    <FaSync className="me-2" />
                    Refresh
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                {loadingStateMachine ? (
                  <div className="loading-container py-4">
                    <Spinner />
                    <p>Loading state machine...</p>
                  </div>
                ) : stateMachine ? (
                  <div>
                    <div className="mb-4">
                      <div className="mb-3">
                        <strong>Name:</strong> {stateMachine.name}
                      </div>
                      <div className="mb-3">
                        <strong>ARN:</strong> {stateMachine.arn}
                      </div>
                      <div className="mb-3">
                        <strong>Status:</strong>{' '}
                        <Badge bg={stateMachine.status === 'ACTIVE' ? 'success' : 'secondary'}>
                          {stateMachine.status}
                        </Badge>
                      </div>
                      <div className="mb-3">
                        <strong>Creation Date:</strong> {new Date(stateMachine.creationDate).toLocaleString()}
                      </div>
                    </div>
                    
                    <h4 className="mb-3">State Machine Diagram</h4>
                    <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '20px', background: '#f8f9fa' }}>
                      {mermaidDiagram ? (
                        <Mermaid chart={mermaidDiagram} />
                      ) : (
                        <div className="text-center">
                          <p>Failed to generate diagram</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4">
                      <h4 className="mb-3">Definition (JSON)</h4>
                      <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                        <pre style={{ background: '#f8f9fa', padding: '15px', borderRadius: '4px', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(stateMachine.definition, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p>No state machine found for this agent in the {selectedEnvironment} environment.</p>
                    <p className="text-muted">Try switching the environment or check if the agent has been deployed.</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>
        )}
      </div>
    </Container>
  );
};

export default MicroAgentDevelopment;